/**
 * MoEngage Queue — runtime constants.
 *
 * All tuneable knobs live here so nothing is scattered across files.
 */

/** Maximum events kept in memory / storage before we start dropping. */
export const MAX_QUEUE_SIZE = 100;

/** How many times a single event may be retried before it is permanently dropped. */
export const MAX_RETRY_ATTEMPTS = 3;

/** First retry waits this long (ms). Subsequent retries double this (exponential backoff). */
export const RETRY_BASE_DELAY_MS = 1_000;

/** Hard cap on any retry delay (ms), regardless of how many retries have occurred. */
export const RETRY_MAX_DELAY_MS = 30_000;

/** Random jitter ceiling added to every retry delay to avoid thundering-herd (ms). */
export const RETRY_JITTER_MAX_MS = 500;

/**
 * After the SDK becomes ready, any events that were queued later (e.g. during
 * an in-progress flush) are picked up on this cadence (ms).
 */
export const FLUSH_INTERVAL_MS = 2_000;

/** How often the readiness poller checks whether the SDK is available (ms). */
export const POLLING_INTERVAL_MS = 500;

/**
 * Maximum polling cycles before we give up waiting for the SDK.
 * 40 cycles × 500 ms = 20 s total wait window.
 */
export const POLLING_MAX_ATTEMPTS = 40;

/** Maximum events sent per flush batch to avoid blocking the main thread. */
export const BATCH_SIZE = 10;

/** localStorage key where the queue is persisted. */
export const STORAGE_KEY = "moengage_event_queue";

/**
 * Custom DOM event name emitted by the MoEngage SDK (or by our own init code)
 * once the SDK is ready. The queue listens for this as a faster alternative to polling.
 */
export const MOENGAGE_READY_EVENT = "moengage_ready";

/**
 * Event priority levels.
 * Lower number = processed first when the queue flushes.
 */
export const EVENT_PRIORITY = Object.freeze({
	HIGH: 1,
	NORMAL: 2,
	LOW: 3,
});

/**
 * Lifecycle states for individual queued events.
 * Used to prevent double-processing and to give accurate queue stats.
 */
export const QUEUE_STATUS = Object.freeze({
	PENDING: "pending",
	PROCESSING: "processing",
	FAILED: "failed",
});
